import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import { FaTimes, FaSave, FaSpinner } from 'react-icons/fa';
import { authenticatedFetch } from '../utils/tokenUtils';
import ReactQuill from 'react-quill';

const EditDocumentModal = ({ 
  isOpen, 
  onClose, 
  publicationId,
  onSuccess 
}) => {
  const [htmlContent, setHtmlContent] = useState('');
  const [originalHtml, setOriginalHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [documentTitle, setDocumentTitle] = useState('');
  const quillRef = useRef(null);

  useEffect(() => {
    if (isOpen && publicationId) {
      fetchHtmlContent();
    } else if (!isOpen) {
      // Reset when modal closes
      setHtmlContent('');
      setOriginalHtml('');
      setError(null);
    }
  }, [isOpen, publicationId]);

  // Update Quill editor when HTML content is loaded
  useEffect(() => {
    if (htmlContent && quillRef.current && !loading) {
      const quill = quillRef.current.getEditor();
      if (quill) {
        // Only update if content is different to avoid infinite loops
        const currentContent = quill.root.innerHTML;
        if (currentContent !== htmlContent && htmlContent.trim() !== '') {
          quill.clipboard.dangerouslyPasteHTML(htmlContent);
        }
      }
    }
  }, [htmlContent, loading]);

  const fetchHtmlContent = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`/api/privacy-policy/edit/${publicationId}`);
      if (response.ok) {
        const data = await response.json();
        setHtmlContent(data.htmlContent || '');
        setOriginalHtml(data.htmlContent || '');
        setDocumentTitle(data.documentTitle || 'Document');
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to load document');
      }
    } catch (err) {
      console.error('Error fetching HTML:', err);
      setError('Failed to load document content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Get HTML content from Quill editor
    const quill = quillRef.current?.getEditor();
    if (!quill) {
      setError('Editor not ready');
      return;
    }

    const editorContent = quill.root.innerHTML;
    
    if (!editorContent.trim() || editorContent === '<p><br></p>') {
      setError('Document content cannot be empty');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      // Wrap content in body tag structure if needed
      const bodyContent = editorContent;
      
      const response = await authenticatedFetch(`/api/privacy-policy/edit/${publicationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ htmlContent: bodyContent }),
      });

      if (response.ok) {
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to save document');
      }
    } catch (err) {
      console.error('Error saving document:', err);
      setError('Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all changes?')) {
      const quill = quillRef.current?.getEditor();
      if (quill) {
        quill.root.innerHTML = originalHtml;
      }
      setHtmlContent(originalHtml);
      setError(null);
    }
  };

  // Quill editor modules configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'color', 'background',
    'align',
    'link', 'image'
  ];

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="w-full max-w-6xl bg-gray-900 rounded-lg shadow-xl border border-gray-800 flex flex-col max-h-[90vh]">
              <div className="flex items-center justify-between p-6 border-b border-gray-800">
                <DialogTitle className="text-xl font-semibold text-white">
                  Edit Document: {documentTitle}
                </DialogTitle>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <FaTimes className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-hidden flex flex-col p-6 relative">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <FaSpinner className="h-8 w-8 text-indigo-600 animate-spin" />
                    <span className="ml-3 text-gray-400">Loading document...</span>
                  </div>
                ) : error ? (
                  <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 text-red-400">
                    {error}
                  </div>
                ) : (
                  <>
                    <div className="mb-4 flex items-center justify-between">
                      <p className="text-sm text-gray-400">
                        Edit the document content below. Changes will be saved to both the online version and PDF.
                      </p>
                      <button
                        onClick={handleReset}
                        className="px-3 py-1.5 text-sm bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors"
                      >
                        Reset Changes
                      </button>
                    </div>
                    <div className="flex-1 overflow-auto relative" style={{ minHeight: '400px' }}>
                      <style>{`
                        .ql-container {
                          font-family: inherit;
                          font-size: 14px;
                          height: auto;
                          min-height: 400px;
                        }
                        .ql-editor {
                          min-height: 400px;
                          color: #e5e7eb;
                          background-color: #1f2937;
                        }
                        .ql-toolbar {
                          position: sticky;
                          top: 0;
                          z-index: 10;
                          background-color: #374151;
                          border: 1px solid #4b5563;
                          border-radius: 0.5rem 0.5rem 0 0;
                          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                        }
                        .ql-container {
                          border: 1px solid #4b5563;
                          border-top: none;
                          border-radius: 0 0 0.5rem 0.5rem;
                        }
                        .ql-snow .ql-stroke {
                          stroke: #9ca3af;
                        }
                        .ql-snow .ql-fill {
                          fill: #9ca3af;
                        }
                        .ql-snow .ql-picker-label {
                          color: #9ca3af;
                        }
                        .ql-snow .ql-picker-options {
                          background-color: #374151;
                          border: 1px solid #4b5563;
                          z-index: 20;
                        }
                        .ql-snow .ql-picker-item {
                          color: #e5e7eb;
                        }
                        .ql-snow .ql-picker-item:hover {
                          background-color: #4b5563;
                        }
                      `}</style>
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={htmlContent || ''}
                        onChange={(content, delta, source, editor) => {
                          setHtmlContent(editor.getHTML());
                        }}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Edit document content here..."
                        style={{ 
                          backgroundColor: '#1f2937',
                          color: '#e5e7eb'
                        }}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-800">
                <button
                  onClick={onClose}
                  disabled={saving}
                  className="px-4 py-2 text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || loading || !htmlContent.trim()}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <FaSpinner className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditDocumentModal;
